import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { RootState, store } from "../../utils/store";
import { Spinner } from "../../utils/Spinner";
import { Button, Icon } from "../../components/tailwind";
import { Option } from "../../components/tailwind/dropdown/Dropdown";
import { ImageCarousel } from "./ImageCarousel";
import { EnrichFieldRenderer } from "./EnrichFieldRenderer";
import { EnrichFieldsetSelector } from "./EnrichFieldsetSelector";
import { useGetProductQuery } from "../../api/productDetailSlice";
import { useGetCustomerQuery } from "../../api/customerApi";
import {
  useEnrichFromFieldsetMutation,
  useGetProductEnrichViewDataQuery,
  useGetLatestEnrichResultQuery,
  useUpdateEnrichResultFieldMutation,
  useRevertEnrichResultFieldMutation,
  useUpdateEnrichInputOverrideMutation,
  useSendEnrichToPimMutation,
  EnrichStatus,
} from "../../api/enrichApi";
import { Job } from "../../customers/jobs/types";
import { JobProgressbarHandler } from "../../customers/jobs/JobProgressbarHandler";
import { SSEClient } from "../../api/SSEApi";
import {
  NotificationAppearance,
  setDjangoToastOpen,
} from "../../api/djangoToastSlice";

type Props = {
  productId: string;
};

export const ProductEnrichView: React.FC<Props> = ({ productId }) => {
  const token = useSelector((state: RootState) => state.auth.token);

  const { data: product, isLoading: isProductLoading } = useGetProductQuery(
    productId
  );
  const { data: customer } = useGetCustomerQuery();
  const {
    data: pageData,
    isLoading: isDataLoading,
    error: dataError,
  } = useGetProductEnrichViewDataQuery(productId);

  const {
    data: latestResult,
    isLoading: isResultLoading,
    refetch: refetchLatestResult,
  } = useGetLatestEnrichResultQuery(productId);

  const [
    enrichFromFieldset,
    { isLoading: isEnriching },
  ] = useEnrichFromFieldsetMutation();

  const [updateEnrichResultField] = useUpdateEnrichResultFieldMutation();
  const [revertEnrichResultField] = useRevertEnrichResultFieldMutation();
  const [
    updateEnrichInputOverride,
    { isLoading: isSavingInputOverride },
  ] = useUpdateEnrichInputOverrideMutation();
  const [sendEnrichToPim, { isLoading: isSendingToPim }] =
    useSendEnrichToPimMutation();

  const [selectedFieldset, setSelectedFieldset] = useState<number | null>(null);
  const [activeJobId, setActiveJobId] = useState<number | null>(null);
  const [enrichingJob, setEnrichingJob] = useState<Job | null>(null);
  const [streamIsActive, setStreamIsActive] = useState(false);
  const [editableWebIdentification, setEditableWebIdentification] = useState<
    Record<string, string>
  >({});
  const [editableSupplierText, setEditableSupplierText] = useState("");

  // Set initial fieldset when pageData loads
  useEffect(() => {
    if (pageData?.initial_fieldset_id && !selectedFieldset) {
      setSelectedFieldset(pageData.initial_fieldset_id);
    }
  }, [pageData?.initial_fieldset_id]);

  // Auto-resume streaming if there's an active job when page loads
  useEffect(() => {
    if (pageData?.active_job_id && !activeJobId) {
      setActiveJobId(pageData.active_job_id);
    }
  }, [pageData?.active_job_id]);

  useEffect(() => {
    if (!pageData) {
      return;
    }

    setEditableWebIdentification(
      Object.fromEntries(
        Object.entries(pageData.web_identification).map(([key, value]) => [
          key,
          String(value ?? ""),
        ])
      )
    );
    setEditableSupplierText(pageData.supplier_text || "");
  }, [pageData?.web_identification, pageData?.supplier_text]);

  // SSE stream management
  const startStream = useCallback((): { close: () => void } => {
    if (!activeJobId || !token || streamIsActive) {
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      return { close: (): void => {} };
    }

    const sseClient = new SSEClient<Job>(token);
    setStreamIsActive(true);

    sseClient.connect(
      `/api/_internal/customer/jobs/${activeJobId}/stream/`,
      (data) => {
        setEnrichingJob(data);
      },
      () => {
        // On close: cleanup and refetch latest result
        setStreamIsActive(false);
        setActiveJobId(null);
        setEnrichingJob(null);
        refetchLatestResult();
      },
      (error, msg) => {
        // On error: show notification with link to job detail
        setStreamIsActive(false);
        store.dispatch(
          setDjangoToastOpen({
            appearance: NotificationAppearance.ERROR,
            content: msg || "Something went wrong with the enrichment stream.",
            additionalContent: `Check Job ${activeJobId} for more information.`,
          })
        );
        // Keep activeJobId so user can see it failed
      }
    );

    return {
      close: (): void => {
        sseClient.close();
        setStreamIsActive(false);
      },
    };
  }, [activeJobId, token, streamIsActive, refetchLatestResult]);

  useEffect(() => {
    if (!activeJobId || !token) {
      return;
    }
    const { close } = startStream();
    return () => {
      close();
    };
  }, [activeJobId, token]);

  const handleEnrich = async (): Promise<void> => {
    if (!selectedFieldset) {
      alert("Please select an Input Schema first");
      return;
    }

    try {
      const result = await enrichFromFieldset({
        product_id: productId,
        fieldset_id: selectedFieldset,
        add_reasoning: true,
      }).unwrap();

      // Start streaming the job progress
      setActiveJobId(result.job_id);
    } catch (error) {
      console.error("Enrichment failed:", error);
      alert("Enrichment failed. Please try again.");
    }
  };

  const handleSaveField = async (
    fieldKey: string,
    fieldValue: any
  ): Promise<void> => {
    if (!latestResult) return;

    await updateEnrichResultField({
      product_id: productId,
      enrich_result_id: latestResult.id,
      field_key: fieldKey,
      field_value: fieldValue,
    }).unwrap();
  };

  const handleRevertField = async (fieldKey: string): Promise<void> => {
    if (!latestResult) return;

    await revertEnrichResultField({
      product_id: productId,
      enrich_result_id: latestResult.id,
      field_key: fieldKey,
    }).unwrap();
  };

  const getStatusBadgeColor = (status: EnrichStatus): string => {
    switch (status) {
      case EnrichStatus.NOT_ENRICHED:
        return "tw-bg-yellow-100 tw-text-yellow-800 tw-border-yellow-300";
      case EnrichStatus.ENRICHED:
        return "tw-bg-green-100 tw-text-green-800 tw-border-green-300";
      case EnrichStatus.ENRICHED_AND_SENT_TO_PIM:
        return "tw-bg-blue-100 tw-text-blue-800 tw-border-blue-300";
      default:
        return "tw-bg-gray-100 tw-text-gray-800 tw-border-gray-300";
    }
  };

  const formatTimestamp = (timestamp: string): string => {
    return new Date(timestamp).toISOString().replace("T", " ").slice(0, 19);
  };

  const buildJobUrl = (customerSlug: string, jobId: number): string => {
    return `/d/${customerSlug}/manage/jobs/${jobId}`;
  };

  const getFieldsetOptions = (): Option<number>[] => {
    if (!pageData?.fieldsets) return [];
    return pageData.fieldsets.map(([id, name]) => ({
      key: String(id),
      value: id,
      text: name,
    }));
  };

  const hasWebIdentificationChanges = (): boolean => {
    if (!pageData) return false;
    const currentEntries = Object.entries(
      pageData.web_identification
    ).map(([key, value]) => [key, String(value ?? "")]);
    const editableEntries = Object.entries(
      editableWebIdentification
    ).map(([key, value]) => [key, String(value ?? "")]);
    return JSON.stringify(currentEntries) !== JSON.stringify(editableEntries);
  };

  const hasSupplierTextChanges = (): boolean => {
    if (!pageData) return false;
    return (pageData.supplier_text || "") !== editableSupplierText;
  };

  const handleSaveWebIdentification = async (): Promise<void> => {
    try {
      await updateEnrichInputOverride({
        product_id: productId,
        web_identification: editableWebIdentification,
      }).unwrap();
    } catch (error) {
      console.error("Failed to save web identification:", error);
      alert("Failed to save web identification. Please try again.");
    }
  };

  const handleSaveSupplierText = async (): Promise<void> => {
    try {
      await updateEnrichInputOverride({
        product_id: productId,
        supplier_text: editableSupplierText,
      }).unwrap();
    } catch (error) {
      console.error("Failed to save supplier text:", error);
      alert("Failed to save supplier text. Please try again.");
    }
  };

  if (isProductLoading || isDataLoading || isResultLoading) {
    return (
      <div className="tw-flex tw-justify-center tw-items-center tw-min-h-screen">
        <Spinner />
      </div>
    );
  }

  if (dataError || !pageData) {
    return (
      <div className="tw-p-4 tw-bg-red-100 tw-text-red-700 tw-rounded">
        {dataError ? "Failed to fetch enrich data" : "Failed to load data"}
      </div>
    );
  }

  return (
    <div className="tw-container tw-mx-auto tw-p-6">
      <div className="tw-flex tw-justify-between tw-mb-6 tw-items-center">
        <div className="tw-flex tw-gap-6 tw-items-center">
          <h1>Enrich</h1>
          <Button
            variant="textual-blue-alt"
            onClick={() =>
              (window.location.href = `/d/${customer?.slug}/product/${product.id}`)
            }
          >
            <span className="tw-flex tw-items-center tw-gap-2">
              <Icon name="edit" /> Texts View...
            </span>
          </Button>
        </div>
        <div className="tw-flex tw-gap-4">
          {/* Header buttons */}
          <Button
            variant="secondary-alt"
            onClick={() =>
              (window.location.href = `/d/${customer?.slug}/manage/enrich-prompt`)
            }
          >
            <span className="tw-flex tw-items-center tw-gap-2">
              <Icon name="settings" /> Settings
            </span>
          </Button>
          <div>
            <Button
              variant="textual-blue"
              disabled={!!activeJobId || !latestResult || isSendingToPim}
              onClick={async () => {
                if (!latestResult) {
                  return;
                }
                try {
                  await sendEnrichToPim({
                    product_id: productId,
                    enrich_result_id: latestResult.id,
                  }).unwrap();
                  store.dispatch(
                    setDjangoToastOpen({
                      appearance: NotificationAppearance.SUCCESS,
                      content: "Sent to PIM",
                    }),
                  );
                } catch (error: any) {
                  store.dispatch(
                    setDjangoToastOpen({
                      appearance: NotificationAppearance.ERROR,
                      content: error?.data?.error || "Failed to send to PIM",
                    }),
                  );
                }
              }}
            >
              <span className="tw-flex tw-items-center tw-gap-2">
                <Icon name="send" /> Send to PIM
              </span>
            </Button>
            {latestResult?.published && (
              <p className="tw-text-xs tw-text-gray-500 tw-m-0 tw-p-0">
                Last sent: {formatTimestamp(latestResult?.published)}
              </p>
            )}
          </div>
        </div>
      </div>
      <div className="txu-divider"></div>
      {/* Header Bar */}
      <div className="tw-bg-white tw-shadow tw-rounded-lg tw-p-4 tw-mb-6 tw-flex tw-justify-between">
        <div>
          <p className="tw-text-gray-500 tw-text-sm tw-mb-0 tw-pb-0">ID</p>
          <p className="tw-mt-0 tw-pt-0">{product.external_id || product.id}</p>
        </div>
        <div>
          <p className="tw-text-gray-500 tw-text-sm tw-mb-0 tw-pb-0">Created</p>
          <p className="tw-mt-0 tw-pt-0">
            {formatTimestamp(product.created_at)}
          </p>
        </div>
        <div>
          <p className="tw-text-gray-500 tw-text-sm tw-mb-0 tw-pb-0">Status</p>
          <span
            className={`tw-px-3 tw-py-1 tw-rounded-full tw-text-sm tw-font-medium tw-border ${getStatusBadgeColor(
              pageData.status
            )}`}
          >
            {pageData.status}
          </span>
        </div>
      </div>

      {/* Input for Enrichment Section */}
      <div className="tw-mb-6">
        <p className="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-0 tw-pb-0">
          Input for Enrichment
        </p>
        <p className="tw-text-gray-500 tw-text-sm tw-mt-0 tw-pt-0">
          The data that will be sent to the AI to use as a basis for the
          enrichment
        </p>

        <div className="tw-grid tw-grid-cols-3 tw-gap-4">
          {/* Web Identification */}
          <div className="tw-bg-white tw-shadow tw-rounded-lg tw-p-4">
            <h3 className="tw-text-lg tw-font-semibold tw-mb-3 tw-text-gray-900">
              Web Identification
            </h3>
            {Object.keys(pageData.web_identification).length > 0 ? (
              <div className="tw-space-y-2">
                {Object.entries(pageData.web_identification).map(
                  ([key, value]) => (
                    <div
                      key={key}
                      className="tw-border-b tw-border-gray-200 tw-pb-2 last:tw-border-b-0"
                    >
                      <div className="tw-text-sm tw-font-medium tw-text-gray-600">
                        {key}
                      </div>
                      <input
                        type="text"
                        value={
                          editableWebIdentification[key] ?? String(value ?? "")
                        }
                        onChange={(event) => {
                          const nextValue = event.target.value;
                          setEditableWebIdentification((prev) => ({
                            ...prev,
                            [key]: nextValue,
                          }));
                        }}
                        className="tw-w-full tw-text-sm tw-text-gray-900 tw-mt-1 tw-px-2 tw-py-1 tw-border tw-border-gray-300 tw-rounded"
                        disabled={!!activeJobId || isSavingInputOverride}
                      />
                    </div>
                  )
                )}
                <div className="tw-flex tw-justify-end tw-mt-3">
                  <Button
                    variant="textual-blue"
                    size="sm"
                    compact="very"
                    onClick={handleSaveWebIdentification}
                    disabled={
                      !hasWebIdentificationChanges() ||
                      !!activeJobId ||
                      isSavingInputOverride
                    }
                  >
                    Save
                  </Button>
                </div>
              </div>
            ) : (
              <p className="tw-text-gray-500 tw-text-sm">
                No web identification fields configured
              </p>
            )}
          </div>

          {/* Product Images */}
          <div className="tw-bg-white tw-shadow tw-rounded-lg tw-p-4">
            <h3 className="tw-text-lg tw-font-semibold tw-mb-3 tw-text-gray-900">
              Product Images
            </h3>
            {pageData.image_urls.length > 0 ? (
              <ImageCarousel images={pageData.image_urls} />
            ) : (
              <p className="tw-text-gray-500 tw-text-sm">No images available</p>
            )}
          </div>

          {/* Supplier Text */}
          <div className="tw-bg-white tw-shadow tw-rounded-lg tw-p-4">
            <h3 className="tw-text-lg tw-font-semibold tw-mb-3 tw-text-gray-900">
              {pageData.supplier_text_field_id || "Supplier Text"}
            </h3>
            <textarea
              value={editableSupplierText}
              onChange={(event) => setEditableSupplierText(event.target.value)}
              className="tw-w-full tw-min-h-[160px] tw-text-sm tw-text-gray-900 tw-px-2 tw-py-1 tw-border tw-border-gray-300 tw-rounded"
              placeholder="No supplier text available"
              disabled={!!activeJobId || isSavingInputOverride}
            />
            <div className="tw-flex tw-justify-end tw-mt-3">
              <Button
                variant="textual-blue"
                size="sm"
                compact="very"
                onClick={handleSaveSupplierText}
                disabled={
                  !hasSupplierTextChanges() ||
                  !!activeJobId ||
                  isSavingInputOverride
                }
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Fieldset Selector */}
      <EnrichFieldsetSelector
        fieldsetOptions={getFieldsetOptions()}
        selectedFieldset={selectedFieldset}
        onFieldsetChange={setSelectedFieldset}
        disabled={!!activeJobId}
        token={token}
      />

      {/* Enrich Result */}
      <div className="tw-mb-6">
        <div className="tw-flex tw-justify-between tw-mb-4">
          <div>
            <p className="tw-text-lg tw-font-semibold tw-text-gray-900 tw-my-0 tw-py-0">
              Enrich Result
            </p>
            <p className="tw-text-gray-500 tw-text-sm tw-my-0 tw-py-0">
              AI-generated product fields with enriched data, values, and
              reasoning based on available information.
            </p>
            {latestResult && latestResult?.job_id && (
              <p className="tw-text-sm tw-my-0 tw-py-0">
                <a
                  href={buildJobUrl(customer?.slug, latestResult.job_id)}
                  target="_blank"
                  rel="noreferrer"
                  className="tw-text-blue-600 hover:tw-underline"
                >
                  Job details...
                </a>
              </p>
            )}
          </div>
          {latestResult && !activeJobId && (
            <>
              <Button
                variant="textual-blue"
                size="sm"
                compact="very"
                className="tw-h-8 tw-my-auto"
                onClick={handleEnrich}
                disabled={!selectedFieldset || isEnriching}
              >
                <span className="tw-flex tw-items-center tw-gap-2">
                  <Icon name="auto_fix_high" /> Enrich again
                </span>
              </Button>
            </>
          )}
        </div>

        {activeJobId ? (
          <div className="tw-bg-white tw-shadow tw-rounded-lg tw-p-6">
            <div className="tw-mb-4">
              <h3 className="tw-text-lg tw-font-semibold tw-text-gray-900">
                Enriching...
              </h3>
              <p className="tw-text-sm tw-text-gray-500">
                Job ID: {activeJobId}
                {" - "}
                <a
                  href={buildJobUrl(customer?.slug, activeJobId)}
                  target="_blank"
                  rel="noreferrer"
                  className="tw-text-blue-600 hover:tw-underline"
                >
                  View details
                </a>
                {streamIsActive && (
                  <span className="tw-text-xs tw-text-gray-500 tw-animate-pulse tw-ml-2">
                    Checking Status...
                  </span>
                )}
              </p>
            </div>
            <JobProgressbarHandler
              job={enrichingJob}
              fetchingJob={!enrichingJob}
              retryClicked={false}
            />
          </div>
        ) : latestResult ? (
          <div className="tw-bg-white tw-shadow tw-rounded-lg tw-overflow-hidden">
            <table className="tw-min-w-full tw-divide-y tw-divide-gray-200">
              <thead className="tw-bg-gray-50">
                <tr>
                  <th className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider tw-w-48">
                    Field
                  </th>
                  <th className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider tw-w-96">
                    Value
                  </th>
                  <th className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider tw-w-48">
                    Reasoning
                  </th>
                  <th className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider tw-w-24">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="tw-bg-white tw-divide-y tw-divide-gray-200">
                {latestResult.enriched_fields.map((field) => (
                  <tr key={field.key} className="hover:tw-bg-gray-50">
                    <td className="tw-px-6 tw-py-4 tw-text-sm tw-font-medium tw-text-gray-900">
                      <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0">
                        <span className="tw-truncate">{field.display_key}</span>
                        <span className="tw-flex-shrink-0">
                          {field.is_edited && (
                            <span className="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-medium tw-bg-blue-100 tw-text-blue-800 tw-whitespace-nowrap">
                              Edited
                            </span>
                          )}
                        </span>
                      </div>
                    </td>
                    <td className="tw-px-6 tw-py-4 tw-text-sm tw-text-gray-900">
                      <EnrichFieldRenderer
                        field={field}
                        onSave={handleSaveField}
                        disabled={!!activeJobId}
                      />
                    </td>
                    <td className="tw-px-6 tw-py-4 tw-text-sm tw-text-gray-600">
                      {field.reasoning || "-"}
                    </td>
                    <td className="tw-px-6 tw-py-4 tw-text-sm">
                      {field.is_edited && (
                        <Button
                          onClick={() => handleRevertField(field.key)}
                          disabled={!!activeJobId}
                          className="tw-p-1 tw-text-gray-500 !tw-bg-transparent hover:!tw-brightness-100 hover:!tw-text-gray-500 !tw-border-gray-500
                            disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                          title="Revert to original AI value"
                          borderless
                        >
                          <Icon name="undo" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="tw-bg-white tw-shadow tw-rounded-lg tw-p-8 tw-flex tw-flex-col tw-items-center tw-justify-center">
            <p className="tw-text-gray-500 tw-text-sm tw-mb-4">
              No enrichment results yet. Select an Input Schema and click Enrich
              to get started.
            </p>
            <Button
              variant="textual-blue"
              onClick={handleEnrich}
              disabled={!selectedFieldset || isEnriching}
            >
              {isEnriching ? (
                "Enriching..."
              ) : (
                <span className="tw-flex tw-items-center tw-gap-2">
                  <Icon name="auto_fix_high" />
                  Enrich
                </span>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
